import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { isEmpty, get } from 'lodash';

import { H3, Paragraph, Text } from 'components/core/Typography';
import { Group } from 'components/core/Page';
import { formatUSD } from 'utils/format';
import { buildFacilityUrl } from 'utils/url';
import { Link } from 'components/core/Link';

const formattedStatRows = [
	{ key: '5x5', label: '5\' x 5\'' },
	{ key: '5x10', label: '5\' x 10\'' },
	{ key: '10x10', label: '10\' x 10\'' },
	{ key: '10x15', label: '10\' x 15\'' },
	{ key: '10x20', label: '10\' x 20\'' },
	{ key: '10x25', label: '10\' x 25\'' },
	{ key: '10x30', label: '10\' x 30\'' },
	{ key: '20', label: "20'" },
	{ key: '25', label: "25'" },
	{ key: '30', label: "30'" },
	{ key: '35', label: "35'" },
	{ key: '40', label: "40'" },
];

const statRows = [
	{ key: '5x5', label: '5 x 5' },
	{ key: '5x10', label: '5 x 10' },
	{ key: '10x10', label: '10 x 10' },
	{ key: '10x15', label: '10 x 15' },
	{ key: '10x20', label: '10 x 20' },
	{ key: '10x25', label: '10 x 25' },
	{ key: '10x30', label: '10 x 30' },
	{ key: '20', label: "20'" },
	{ key: '25', label: "25'" },
	{ key: '30', label: "30'" },
	{ key: '35', label: "35'" },
	{ key: '40', label: "40'" },
];

export const enhance = (WrappedComponent) => {
	const WithPricingStatsProps = (props) => {
		const { pricingStats, isSearchPageVariation } = props;

		const derivedProps = useMemo(() => {
			if (isEmpty(pricingStats)) return null;

			return {
				averagePrice: parseFloat(
					get(pricingStats, 'averagePrices.overall.price', 0),
				).toFixed(2),
				stats: (isSearchPageVariation
					? formattedStatRows
					: statRows
				).reduce((allStats, stat) => {
					const lowest = get(
						pricingStats,
						`lowestPrices.${stat.key}`,
						{},
					);
					const average = get(
						pricingStats,
						`averagePrices.${stat.key}`,
						{},
					);
					if (!isEmpty(lowest) || !isEmpty(average)) {
						return [
							...allStats,
							{
								...stat,
								lowestPrice: get(lowest, 'price'),
								lowestFacility: get(lowest, 'facility'),
								lowestUnit: get(lowest, 'unit'),
								averagePrice: get(average, 'price'),
								lowestUrl: buildFacilityUrl(
									get(lowest, 'facility'),
									get(lowest, 'facilityName'),
									get(lowest, 'facilityCity'),
									get(lowest, 'facilityState'),
								),
							},
						];
					}
					return allStats;
				}, []),
			};
		}, [pricingStats, isSearchPageVariation]);

		if (!derivedProps) return null;

		return (
			<WrappedComponent
				{...props}
				{...derivedProps}
			/>
		);
	};
	return WithPricingStatsProps;
};

export function PricingStats({
	locationName,
	stats,
	averagePrice,
	storageType = 'self-storage',
	city,
	stateAbbreviation
}) {
	return (
		<div className="pricing-stats-container">
			<Group>
				<H3 id="pricing" size="medium">
					Average Price by Storage Unit Size in {city}, {stateAbbreviation}
				</H3>

				<Paragraph>
					Find online move-in rates and specials like first month half
					off and first month FREE at select {locationName} storage
					facilities. Below are the average monthly storage unit
					prices in {locationName} for the most common storage unit
					sizes.
				</Paragraph>
			</Group>
			<table className="content-table">
				<thead>
					<tr className="content-table-row">
						<th className="content-table-header">
							<Text size="small">Unit Size</Text>
						</th>
						<th className="content-table-header">
							<Text size="small">Average Price</Text>
						</th>
						<th className="content-table-header">
							<Text size="small">Lowest Price</Text>
						</th>
					</tr>
				</thead>
				<tbody className="content-table-body">
					{stats.map((stat) => (
						<tr
							key={stat.key}
							className="content-table-row"
						>
							<td className="content-table-cell">
								<Text color="gray">{stat.label}</Text>
							</td>
							<td className="content-table-cell">
								<Text color="gray">
									{stat.averagePrice
										? formatUSD(stat.averagePrice)
										: 'Not Available'}
								</Text>
							</td>
							<td className="content-table-cell">
								{!!stat.lowestPrice && (
									<Link
										hideBorder
										href={stat.lowestUrl}
										className="lowest"
									>
										<Text color="green">
											{formatUSD(stat.lowestPrice)}
										</Text>
									</Link>
								)}
								{!stat.lowestPrice && (
									<Text color="gray">Not Available</Text>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>

			{averagePrice > 0 && (
				<Paragraph>
					The overall{' '}
					<Link
						href="https://www.sparefoot.com/blog/16906-how-much-does-a-storage-unit-cost/"
						target="_blank"
					>
						average price of a {storageType} unit{' '}
					</Link>
					booked in {locationName} over the past 180 days is $
					{averagePrice}.
				</Paragraph>
			)}
			<Paragraph last>
				Data for the cheapest {storageType} unit is updated every 15
				minutes.
			</Paragraph>
		</div>
	);
}

PricingStats.propTypes = {
	city: PropTypes.string,
	stateAbbreviation: PropTypes.string,
	stats: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string,
			label: PropTypes.string,
			lowestFacility: PropTypes.number,
			lowestUnit: PropTypes.number,
			lowestPrice: PropTypes.string,
			averagePrice: PropTypes.number,
		}),
	).isRequired,
	storageType: PropTypes.string,
	locationName: PropTypes.string.isRequired,
	averagePrice: PropTypes.string.isRequired,
};

export default enhance(PricingStats);
